import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, X, Trophy, Compass } from "lucide-react";
import { api } from "../lib/api";
import { shojinApi } from "../lib/shojinApi";
import { trackEvent } from "../lib/analytics";

export default function CompleteWorkoutSheet({ open, onClose, source = "daily_call", title = "", exercises = [], onCompleted }) {
  const [duration, setDuration] = useState(30);
  const [rpe, setRpe] = useState(7);
  const [notes, setNotes] = useState("");
  const [reflection, setReflection] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.completeWorkout({
        source, title, duration_min: duration, rpe, notes,
        exercises_done: exercises.map((e) => (typeof e === "string" ? e : e.name)).filter(Boolean),
      });
      try { await api.streakPing("workout_complete"); } catch {}
      // Auto-log the Shōjin reflection to the doubt diary if the user wrote one.
      const trimmed = (reflection || "").trim();
      if (trimmed) {
        try {
          await shojinApi.addDoubt(`Showed up despite ${trimmed}`);
          trackEvent("shojin_reflection_logged", { length: trimmed.length });
        } catch { /* non-critical */ }
      }
      setDone(true);
      setTimeout(() => {
        setBusy(false); setDone(false); setReflection("");
        onCompleted && onCompleted();
        onClose && onClose();
      }, 900);
    } catch {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      onClick={onClose}
      className="fixed inset-0 z-[10010] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
      data-testid="complete-workout-sheet"
    >
      <motion.div
        initial={{ y: 40 }} animate={{ y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-t-3xl sm:rounded-3xl p-6 text-white max-h-[88vh] overflow-y-auto"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)' }}
      >
        {done ? (
          <div className="py-8 text-center" data-testid="complete-workout-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-lime-400 grid place-items-center">
              <Check className="h-8 w-8 text-black" strokeWidth={3} />
            </div>
            <h3 className="mt-4 text-xl font-semibold">Logged. Good work.</h3>
            <p className="text-sm text-zinc-400 mt-1">Streak protected.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-lime-400">Complete workout</div>
                <h3 className="mt-1 text-lg font-semibold">{title || "Today's session"}</h3>
              </div>
              <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-full hover:bg-zinc-800" data-testid="complete-workout-close">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-5">
              <label className="text-xs uppercase tracking-wider text-zinc-400">Duration ({duration} min)</label>
              <input
                type="range" min="5" max="120" value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                data-testid="complete-workout-duration"
                className="w-full mt-2 accent-lime-400"
              />
            </div>

            <div className="mt-4">
              <label className="text-xs uppercase tracking-wider text-zinc-400">
                Effort (RPE {rpe} — {rpe <= 4 ? "easy" : rpe <= 6 ? "moderate" : rpe <= 8 ? "hard" : "max"})
              </label>
              <div className="mt-2 grid grid-cols-10 gap-1">
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => setRpe(n)}
                    data-testid={`complete-workout-rpe-${n}`}
                    className={`h-9 rounded-md text-xs font-semibold transition-colors ${
                      n === rpe ? "bg-lime-400 text-black" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                    }`}
                  >{n}</button>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <label className="text-xs uppercase tracking-wider text-zinc-400">Notes (optional)</label>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="How did it feel?"
                data-testid="complete-workout-notes"
                className="w-full mt-2 bg-zinc-950 border border-zinc-800 focus:border-lime-400 rounded-lg px-3 py-2 text-sm text-white outline-none"
              />
            </div>

            <div className="mt-4 rounded-xl border border-[#BEFF38]/20 bg-[#BEFF38]/5 p-3">
              <div className="flex items-center gap-1.5">
                <Compass className="w-3 h-3 text-[#BEFF38]" strokeWidth={2.5} />
                <label className="text-[10px] uppercase tracking-[0.16em] font-black text-[#BEFF38]">Shōjin reflection · optional</label>
              </div>
              <div className="mt-2 flex items-baseline gap-1 text-sm">
                <span className="text-zinc-400 italic shrink-0">Today I showed up despite</span>
              </div>
              <input
                type="text"
                value={reflection}
                onChange={(e) => setReflection(e.target.value.slice(0, 140))}
                placeholder="tiredness / a hard day / not feeling like it…"
                data-testid="complete-workout-reflection"
                className="mt-1.5 w-full bg-zinc-950 border border-zinc-800 focus:border-[#BEFF38] rounded-lg px-3 py-2 text-sm text-white outline-none"
              />
              <p className="mt-1.5 text-[10px] text-zinc-500 leading-snug">Logged to your Doubt Diary. Patterns become visible over time.</p>
            </div>

            <button
              onClick={submit}
              disabled={busy}
              data-testid="complete-workout-submit"
              className="mt-5 w-full rounded-full h-12 bg-lime-400 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4" />}
              {busy ? "Saving…" : "Log workout"}
            </button>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
